import {HTMLReactParserOptions, Element as ParserElement} from 'html-react-parser';
import {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, ClientDynamicContent, LanguageSwitcher, NodeElement, SessionTextChatProps} from 'components';
import {DynamicComponentNames, FullScreenAsyncRequestsUnits as Units, FULL_SCREEN_ASYNC_REQUEST_TEMPLATE} from 'constants/client';
import {ClientContext} from 'context';
import {useClientSession} from 'hooks';
import {AsyncRequestStatusName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {dynamicContentUtils, imageUtils} from 'utils';

import {ClientAsyncRequest} from '../ClientAsyncRequest';

import './styles.scss';

const {isMatchElement, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {image, renderCancelButton, localParticipant} = props;
    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;

            if (isMatchElement(Units.Logo, node)) {
                return <NodeElement node={node}><img className='logo-img' src={image || imageUtils.getLogo()} alt='Logo' /></NodeElement>;
            }
            if (isMatchElement(Units.Language, node)) {
                return <NodeElement node={node}><LanguageSwitcher className='language-switcher' hidePlaceholder /></NodeElement>;
            }
            if (isMatchElement(Units.CancelButton, node)) {
                return <NodeElement node={node}>{renderCancelButton()}</NodeElement>;
            }
            if (isMatchElement(Units.AsyncRequest, node)) {
                return (
                    <NodeElement node={node}>
                        <ClientAsyncRequest />
                    </NodeElement>);
            }
            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

type Props = {
    TextChatComponent?: React.ComponentType<SessionTextChatProps>;
};

export const FullScreenAsyncRequest: React.FC<Props> = ({TextChatComponent}: Props) => {
    const {t} = useTranslation();
    const {image, asyncRequestToHandle} = useClientSession();
    const {updateParticipantAsyncRequest} = useContext(ClientContext);

    const cancelLabel = useMemo(() => t(TRANSLATION_KEYS.COMMON.BUTTONS.CANCEL), [t]);

    const handleCancel = useCallback(() => {
        updateParticipantAsyncRequest(asyncRequestToHandle.type, AsyncRequestStatusName.Failed, {});
    }, [asyncRequestToHandle?.type, updateParticipantAsyncRequest]);

    const renderCancelButton = useCallback(() =>
        <Button color='attention' onClick={handleCancel}>{cancelLabel}</Button>,
    [cancelLabel, handleCancel]);

    return (
        <ClientDynamicContent
            component={DynamicComponentNames.FullScreenAsyncRequest}
            defaultTemplate={FULL_SCREEN_ASYNC_REQUEST_TEMPLATE}
            optionsProps={{TextChatComponent, image, renderCancelButton}}
            optionsGetter={getOptions}
        />
    );
};
